(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 798px)';
  const ACTIVE_CLASS = 'b2-search-only-sticky';
  const PLACEHOLDER_CLASS = 'b2-mobile-inline-search-placeholder';
  const SENTINEL_CLASS = 'b2-mobile-search-sentinel';
  const HEIGHT_VAR = '--b2-mobile-search-placeholder-height';

  window.B2SearchOnlySticky = window.B2SearchOnlySticky || {};
  const manager = window.B2SearchOnlySticky;
  const BROWSE_STORAGE_PREFIX = 'b2CollectionBrowse:v2:';
  const BROWSE_TTL = 30 * 60 * 1000;
  const PHASE_DEPARTING = 'DEPARTING_COLLECTION';
  const PHASE_PRODUCT_SEEN = 'PRODUCT_SEEN';

  function markPendingProductSeen() {
    if (!window.location.pathname.startsWith('/products/')) return;

    let keys = [];
    try {
      keys = Array.from({ length: window.sessionStorage.length }, (_, index) =>
        window.sessionStorage.key(index)
      ).filter((key) => key && key.startsWith(BROWSE_STORAGE_PREFIX));
    } catch (_) {
      return;
    }

    keys.forEach((key) => {
      try {
        const payload = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (!payload) return;
        if (Date.now() - payload.savedAt > BROWSE_TTL) {
          window.sessionStorage.removeItem(key);
          return;
        }

        const phase = payload.phase || PHASE_DEPARTING;
        if (
          phase === PHASE_DEPARTING &&
          payload.productPath === window.location.pathname
        ) {
          payload.phase = PHASE_PRODUCT_SEEN;
          payload.productSeenAt = Date.now();
          payload.pendingReturn = true;
          window.sessionStorage.setItem(key, JSON.stringify(payload));
        }
      } catch (_) {}
    });
  }


  function cleanupBindings() {
    if (manager.scrollHandler) {
      window.removeEventListener('scroll', manager.scrollHandler);
      manager.scrollHandler = null;
    }
    if (manager.resizeHandler) {
      window.removeEventListener('resize', manager.resizeHandler);
      manager.resizeHandler = null;
    }
    if (manager.resizeObserver) {
      manager.resizeObserver.disconnect();
      manager.resizeObserver = null;
    }
    if (Array.isArray(manager.timers)) {
      manager.timers.forEach((timer) => window.clearTimeout(timer));
    }
    manager.timers = [];
  }

  function resetVisualState() {
    const body = document.body;
    if (!body) return;
    body.classList.remove(ACTIVE_CLASS);
    body.style.removeProperty(HEIGHT_VAR);
    const header = document.getElementById('header');
    if (header) {
      header.querySelectorAll('.' + SENTINEL_CLASS + ', .' + PLACEHOLDER_CLASS).forEach((node) => node.remove());
    }
  }

  function init() {
    markPendingProductSeen();
    cleanupBindings();
    resetVisualState();

    const generation = (manager.generation || 0) + 1;
    manager.generation = generation;
    manager.timers = [];

    const body = document.body;
    const header = document.getElementById('header');
    const search = header && header.querySelector('[data-b2-mobile-inline-search]');

    if (!body || !header || !search) return;

    const media = window.matchMedia(MOBILE_QUERY);

    header.querySelectorAll('.' + SENTINEL_CLASS + ', .' + PLACEHOLDER_CLASS).forEach((node) => node.remove());

    const sentinel = document.createElement('span');
    sentinel.className = SENTINEL_CLASS;
    sentinel.setAttribute('aria-hidden', 'true');

    const placeholder = document.createElement('div');
    placeholder.className = PLACEHOLDER_CLASS;
    placeholder.setAttribute('aria-hidden', 'true');

    search.insertAdjacentElement('beforebegin', sentinel);
    search.insertAdjacentElement('afterend', placeholder);

    let anchorY = 0;
    let normalHeight = 0;
    let active = false;
    let ticking = false;

    const isCurrent = () =>
      manager.generation === generation &&
      document.body === body &&
      search.isConnected &&
      sentinel.isConnected;

    const measureNormalState = () => {
      if (!isCurrent()) return;
      if (!media.matches || active || body.classList.contains('is-active')) return;

      const searchRect = search.getBoundingClientRect();
      const sentinelRect = sentinel.getBoundingClientRect();

      if (searchRect.height <= 0) return;

      normalHeight = searchRect.height;
      anchorY = sentinelRect.top + window.scrollY;
      body.style.setProperty(HEIGHT_VAR, normalHeight.toFixed(2) + 'px');
    };

    const activate = () => {
      if (!isCurrent() || active) return;
      if (!normalHeight) measureNormalState();
      if (normalHeight) {
        body.style.setProperty(HEIGHT_VAR, normalHeight.toFixed(2) + 'px');
      }
      active = true;
      body.classList.add(ACTIVE_CLASS);
    };

    const deactivate = () => {
      active = false;
      body.classList.remove(ACTIVE_CLASS);
      if (isCurrent()) {
        window.requestAnimationFrame(measureNormalState);
      }
    };

    const update = () => {
      ticking = false;
      if (!isCurrent()) return;

      if (!media.matches) {
        deactivate();
        return;
      }

      if (body.classList.contains('is-active')) {
        deactivate();
        return;
      }

      if (window.scrollY <= 1) {
        deactivate();
        measureNormalState();
        return;
      }

      if (!anchorY) measureNormalState();

      if (anchorY > 1 && window.scrollY >= anchorY) {
        activate();
      } else {
        deactivate();
      }
    };

    const scheduleUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    manager.scrollHandler = scheduleUpdate;
    manager.resizeHandler = () => {
      if (active) deactivate();
      window.requestAnimationFrame(() => {
        measureNormalState();
        update();
      });
    };

    window.addEventListener('scroll', manager.scrollHandler, { passive: true });
    window.addEventListener('resize', manager.resizeHandler);

    if ('ResizeObserver' in window) {
      manager.resizeObserver = new ResizeObserver(() => {
        if (!active) {
          measureNormalState();
          scheduleUpdate();
        }
      });
      manager.resizeObserver.observe(search);
    }

    measureNormalState();
    update();
    manager.timers.push(window.setTimeout(() => {
      if (!isCurrent()) return;
      measureNormalState();
      update();
    }, 250));
    manager.timers.push(window.setTimeout(() => {
      if (!isCurrent()) return;
      measureNormalState();
      update();
    }, 1000));
  }

  manager.init = init;
  manager.cleanup = cleanupBindings;

  if (!manager.baseListenersBound) {
    manager.baseListenersBound = true;

    window.addEventListener('pageshow', () => {
      resetVisualState();
      window.requestAnimationFrame(() => manager.init());
    });

    document.addEventListener('shopify:section:load', () => {
      window.setTimeout(() => manager.init(), 0);
    });

    if (window.InstantClick && typeof window.InstantClick.on === 'function') {
      window.InstantClick.on('change', () => {
        resetVisualState();
        window.requestAnimationFrame(() => {
          resetVisualState();
          window.requestAnimationFrame(() => manager.init());
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => manager.init(), { once: true });
  } else {
    manager.init();
  }
})();
